"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Link } from "@/navigation";

// 步骤数据结构
interface ProcessStep {
	titleKey: string;
	descKey: string;
}

// 分类数据结构
interface CategoryData {
	id: string;
	labelKey: string;
	processTitleKey: string;
	image: string;
	steps: ProcessStep[];
}

// 默认分类配置
const DEFAULT_CATEGORIES: CategoryData[] = [
	{
		id: "gear",
		labelKey: "categoryGear",
		processTitleKey: "gearProcessTitle",
		image: "/image/new-version/machining/gear-machining/tech/gear-manufacturing-process.png",
		steps: [
			{ titleKey: "gearStep1Title", descKey: "gearStep1Desc" },
			{ titleKey: "gearStep2Title", descKey: "gearStep2Desc" },
			{ titleKey: "gearStep3Title", descKey: "gearStep3Desc" },
			{ titleKey: "gearStep4Title", descKey: "gearStep4Desc" },
			{ titleKey: "gearStep5Title", descKey: "gearStep5Desc" },
		],
	},
	{
		id: "pulley",
		labelKey: "categoryPulley",
		processTitleKey: "pulleyProcessTitle",
		image: "/image/new-version/machining/gear-machining/tech/pulley-sprockets-manufacturing-process.png",
		steps: [
			{ titleKey: "pulleyStep1Title", descKey: "pulleyStep1Desc" },
			{ titleKey: "pulleyStep2Title", descKey: "pulleyStep2Desc" },
			{ titleKey: "pulleyStep3Title", descKey: "pulleyStep3Desc" },
			{ titleKey: "pulleyStep4Title", descKey: "pulleyStep4Desc" },
			{ titleKey: "pulleyStep5Title", descKey: "pulleyStep5Desc" },
		],
	},
	{
		id: "worm",
		labelKey: "categoryWorm",
		processTitleKey: "wormProcessTitle",
		image: "/image/new-version/machining/gear-machining/tech/worm-gear-manufacturing-process.png",
		steps: [
			{ titleKey: "wormStep1Title", descKey: "wormStep1Desc" },
			{ titleKey: "wormStep2Title", descKey: "wormStep2Desc" },
			{ titleKey: "wormStep3Title", descKey: "wormStep3Desc" },
			{ titleKey: "wormStep4Title", descKey: "wormStep4Desc" },
			{ titleKey: "wormStep5Title", descKey: "wormStep5Desc" },
		],
	},
	{
		id: "track",
		labelKey: "categoryTrack",
		processTitleKey: "trackProcessTitle",
		image: "/image/new-version/machining/gear-machining/tech/track-wheels-rollers-manufacturing-process.png",
		steps: [
			{ titleKey: "trackStep1Title", descKey: "trackStep1Desc" },
			{ titleKey: "trackStep2Title", descKey: "trackStep2Desc" },
			{ titleKey: "trackStep3Title", descKey: "trackStep3Desc" },
			{ titleKey: "trackStep4Title", descKey: "trackStep4Desc" },
			{ titleKey: "trackStep5Title", descKey: "trackStep5Desc" },
		],
	},
	{
		id: "custom",
		labelKey: "categoryCustom",
		processTitleKey: "customProcessTitle",
		image: "/image/new-version/machining/gear-machining/tech/custom-gears.png",
		steps: [
			{ titleKey: "customStep1Title", descKey: "customStep1Desc" },
			{ titleKey: "customStep2Title", descKey: "customStep2Desc" },
			{ titleKey: "customStep3Title", descKey: "customStep3Desc" },
			{ titleKey: "customStep4Title", descKey: "customStep4Desc" },
			{ titleKey: "customStep5Title", descKey: "customStep5Desc" },
		],
	},
];

// 步骤圆圈的颜色配置（渐变绿色系）
const STEP_COLORS = [
	"bg-[#7cb342]", // 绿色
	"bg-[#9ccc65]", // 浅绿
	"bg-[#c5e1a5]", // 更浅绿
	"bg-[#ffb74d]", // 橙色
	"bg-[#ffa726]", // 深橙
];

export interface TechnologyProps {
	translationNamespace?: string;
	categories?: CategoryData[];
	buttonHref?: string;
	className?: string;
}

export default function Technology({
	translationNamespace = "gearTechnology",
	categories = DEFAULT_CATEGORIES,
	buttonHref = "/upload",
	className,
}: TechnologyProps) {
	const t = useTranslations(translationNamespace);
	const [activeCategory, setActiveCategory] = useState(0);

	const currentCategory = categories[activeCategory];

	return (
		<section className={cn("bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("subtitle")}</p>
				</div>

				{/* 分类标签 */}
				<div className="mb-8 flex flex-wrap gap-2 sm:gap-6 md:mb-10 lg:mb-12">
					{categories.map((category, index) => (
						<button
							key={category.id}
							onClick={() => setActiveCategory(index)}
							className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-300 sm:px-4 sm:text-base ${
								activeCategory === index
									? "bg-themePrimary400 text-white [text-shadow:0_0_0.5px_currentColor]"
									: "hover:text-mainColor bg-[#f3f4f6] text-black"
							}`}
						>
							{t(category.labelKey)}
						</button>
					))}
				</div>

				{/* 内容区域 - 左图右文 */}
				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12">
					{/* 左侧图片 */}
					<div className="relative w-full overflow-hidden">
						<Image
							src={currentCategory.image}
							alt={t(currentCategory.processTitleKey)}
							width={1000}
							height={1000}
							className="h-auto w-full"
							sizes="(max-width: 1024px) 100vw, 50vw"
						/>
					</div>

					{/* 右侧流程内容 */}
					<div>
						<h3 className="mb-6 text-lg font-bold text-black sm:text-xl md:mb-8 md:text-2xl">
							{t(currentCategory.processTitleKey)}
						</h3>

						{/* 步骤列表 */}
						<div className="space-y-5 md:space-y-6">
							{currentCategory.steps.map((step, index) => (
								<div key={index} className="flex gap-4">
									{/* 步骤编号圆圈 */}
									<div
										className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#f6ffde] text-sm font-bold text-themePrimary400 sm:h-14 sm:w-14 sm:text-2xl`}
									>
										{index + 1}
									</div>

									{/* 步骤内容 */}
									<div className="flex-1">
										<h4 className="mb-1 text-base font-semibold text-black sm:text-lg">{t(step.titleKey)}</h4>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(step.descKey)}</p>
									</div>
								</div>
							))}
						</div>

						{/* CTA 按钮 */}
						<div className="mt-8 md:mt-10">
							<Link
								href={buttonHref}
								className="hover:bg-mainColorHover inline-block rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-opacity-80 hover:text-white sm:px-8 sm:py-3.5 sm:text-base"
							>
								{t("ctaButton")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
